import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Account } from '../models/account';
import { ApiLog } from '../models/api-log';
import { ApiLogLevel } from '../models/api-log-level';
import { AccountService } from '../services/account.service';
import { ErrorService } from '../services/error.service';
import { TranslationService } from '../services/translation.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private activeRefreshRequest: Promise<Account> | null = null;

  constructor(
    private accountService: AccountService,
    private errorService: ErrorService,
    private tls: TranslationService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // add auth header with jwt if account is logged in and request is to the api url
    const account = this.accountService.accountValue;
    const isLoggedIn = account && account.jwtToken;
    const isApiUrl = request.url.startsWith(environment.apiUrl);
    if (isLoggedIn && isApiUrl) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${account.jwtToken}`,
          Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          'Accept-Language': this.tls.currentLanguage,
        },
      });
    } else {
      request = request.clone({
        setHeaders: {
          Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          'Accept-Language': this.tls.currentLanguage,
        },
      });
    }

    // try the request, catch 401 errors and refresh token, then retry the request (without breaking the observable chain)
    return next.handle(request).pipe(
      catchError((reqError: HttpErrorResponse) => {
        if (reqError.status === 401 && !request.url.includes('/auth/refresh-token')) {
          console.log('401 error, refreshing token');
          if (!this.activeRefreshRequest) {
            this.activeRefreshRequest = this.accountService.refreshToken().then(account => {
              this.activeRefreshRequest = null;
              return account;
            });
          }
          return from(this.activeRefreshRequest)
            .pipe(
              switchMap(() => {
                request = request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${this.accountService.accountValue?.jwtToken}`,
                  },
                });
                return next.handle(request);
              })
            )
            .pipe(
              catchError((refreshError: HttpErrorResponse) => {
                this.accountService.logout();
                throw refreshError;
              })
            );
        } else {
          if (
            ![
              '/authenticate',
              '/register',
              '/refresh-token',
              '/reset-password',
              '/logs',
              '/revoke-token',
            ].some(url => request.url.includes(url))
          ) {
            const apiLog: ApiLog = {
              created: new Date().toISOString(),
              accountId: this.accountService.accountValue?.id || null,
              userAgent: navigator.userAgent,
              level: ApiLogLevel.Error,
              status: reqError.status,
              uiUrl: window.location.href,
              apiUrl: request.url,
              request: JSON.stringify(request),
              response: JSON.stringify(reqError),
            };
            this.errorService.sendLogToApi(apiLog);
          }
          throw reqError;
        }
      })
    );
  }
}
